import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { StreamingSectionSourceOption } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../common/types';
import { sourceLook } from '../streamingSources';
import { formatStreamingNumber } from '../streamingFormat';
import { streamingPeriodMessages as messages } from '../streamingPeriodMessages';
import { SourceMark } from '../parts/SourceMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SectionSourceSwitchProps {
  options: readonly StreamingSectionSourceOption[];
  /** The `key` of the picked option. */
  picked: string | null;
  onPick: (key: string) => void;
}

/**
 * The source switch of a section (design streaming-sections a2, b1): one source at a time. A
 * connected source that does not give the data is dashed and cannot be picked, with the reason.
 * With one option there is no switch.
 */
const SectionSourceSwitchComponent: React.FC<SectionSourceSwitchProps> = ({ options, picked, onPick }) => {
  const intl = useIntl();
  const buttons = useMemo(
    () =>
      options.map((option) => {
        const look = sourceLook(option.integrationType);
        const isOn = option.key === picked;
        const note = !option.gives
          ? intl.formatMessage(
              option.reason === 'notReadYet' ? messages.reasonNotReadYet : messages.reasonDoesNotReport
            )
          : option.store !== null
            ? intl.formatMessage(messages.switchFrom, { source: look.shortName })
            : option.number !== null
              ? formatStreamingNumber(intl, option.number)
              : null;
        return (
          <button
            key={option.key}
            type="button"
            disabled={!option.gives}
            aria-pressed={isOn}
            data-testid={`streaming-source-option-${option.key}`}
            className={clsx(styles.switchOption, isOn && styles.switchOn, !option.gives && styles.switchOff)}
            onClick={() => onPick(option.key)}
          >
            <SourceMark integrationType={option.integrationType} />
            {option.store ?? look.name}
            {note !== null ? <span className={styles.switchNote}>{note}</span> : null}
          </button>
        );
      }),
    [intl, onPick, options, picked]
  );

  if (options.length < 2) {
    return null;
  }
  return (
    <div className={styles.switch} role="group" aria-label={intl.formatMessage(messages.switchLabel)}>
      <span className={styles.switchKey}>{intl.formatMessage(messages.switchLabel)}</span>
      {buttons}
    </div>
  );
};

export const SectionSourceSwitch = TypedMemo(SectionSourceSwitchComponent);
